import React, { useEffect, useState } from 'react';

const MIN_VISIBLE_ROWS = 4;

const SwitchesModal = ({
  isOpen,
  onClose,
  court,
  onSave,
  onTestSwitch,
  shellyServerURL,
  shellyApiKey,
}) => {
  const [switchRows, setSwitchRows] = useState([]);
  const [testResults, setTestResults] = useState({});
  const [globalMessage, setGlobalMessage] = useState({ type: '', text: '' });
  const [isSaving, setIsSaving] = useState(false);

  const parseStoredSwitch = (value) => {
    const raw = String(value || '').trim();
    if (!raw) {
      return { deviceId: '', channel: '' };
    }

    const separatorIndex = raw.lastIndexOf(':');
    if (separatorIndex > 0) {
      const parsedDeviceId = raw.slice(0, separatorIndex).trim();
      const parsedChannel = raw.slice(separatorIndex + 1).trim();
      if (parsedDeviceId && /^\d+$/.test(parsedChannel)) {
        return { deviceId: parsedDeviceId, channel: parsedChannel };
      }
    }

    // Backward compatibility for legacy values stored as plain Device ID.
    return { deviceId: raw, channel: '0' };
  };

  const normalizeChannel = (value) => {
    const digitsOnly = String(value || '').replace(/[^\d]/g, '');
    if (!digitsOnly) return '0';
    return String(Number(digitsOnly));
  };

  const composeSwitchId = ({ deviceId, channel }) => {
    const normalizedDeviceId = String(deviceId || '').trim();
    if (!normalizedDeviceId) return '';
    return `${normalizedDeviceId}:${normalizeChannel(channel)}`;
  };

  const ensureRows = (list) => {
    let updated = [...list];
    while (updated.length < MIN_VISIBLE_ROWS) {
      updated.push({ deviceId: '', channel: '' });
    }
    if (!updated.some((row) => !String(row?.deviceId || '').trim())) {
      updated.push({ deviceId: '', channel: '' });
    }
    return updated;
  };

  useEffect(() => {
    if (isOpen && court) {
      const existing = Array.isArray(court.switches)
        ? court.switches.filter(Boolean).map(parseStoredSwitch)
        : [];
      setSwitchRows(ensureRows(existing));
      setTestResults({});
      setGlobalMessage({ type: '', text: '' });
      setIsSaving(false);
    }
  }, [isOpen, court]);

  const handleDeviceChange = (index, value) => {
    const updated = [...switchRows];
    const previous = updated[index] || { deviceId: '', channel: '' };
    const trimmed = value.trim();
    const shouldDefaultChannelToZero = trimmed && !String(previous.channel || '').trim();

    updated[index] = {
      ...previous,
      deviceId: value,
      channel: shouldDefaultChannelToZero ? '0' : previous.channel
    };
    setSwitchRows(ensureRows(updated));
    setTestResults((prev) => {
      const result = { ...prev };
      delete result[index];
      return result;
    });
  };

  const handleChannelChange = (index, value) => {
    const updated = [...switchRows];
    const previous = updated[index] || { deviceId: '', channel: '' };
    updated[index] = {
      ...previous,
      channel: String(value || '').replace(/[^\d]/g, '')
    };
    setSwitchRows(ensureRows(updated));
    setTestResults((prev) => {
      const result = { ...prev };
      delete result[index];
      return result;
    });
  };

  const formatDeviceStatusMessage = (deviceStatus) => {
    if (!deviceStatus) {
      return 'Unable to verify this switch. Please try again.';
    }

    // If there's an error, show it
    if (deviceStatus.error) {
      return deviceStatus.error;
    }

    // Device not registered
    if (!deviceStatus.exists) {
      return 'Device is not registered in Shelly Cloud.';
    }

    // Device exists but is offline
    if (!deviceStatus.online) {
      return 'Device is registered but the device is OFFLINE.';
    }

    // Device is online but switch is off
    if (!deviceStatus.output) {
      return 'Device is ONLINE but turned OFF.';
    }

    // Device is on with timer
    if (deviceStatus.timerDuration !== null && deviceStatus.timerDuration !== undefined) {
      return `Device is ON and set to turn off in ${deviceStatus.timerDuration} minute${deviceStatus.timerDuration !== 1 ? 's' : ''}.`;
    }

    // Device is on without timer
    return 'Device is ON.';
  };

  const getMessageType = (deviceStatus) => {
    if (!deviceStatus) {
      return 'error';
    }

    // If there's an error, it's an error
    if (deviceStatus.error) {
      return 'error';
    }

    // Device not registered is an error
    if (!deviceStatus.exists) {
      return 'error';
    }

    // Device offline is a warning (could be info or error depending on preference)
    if (!deviceStatus.online) {
      return 'error';
    }

    // Device online and working is success
    return 'success';
  };

  const handleTest = async (index) => {
    setGlobalMessage({ type: '', text: '' });
    const row = switchRows[index] || { deviceId: '', channel: '' };
    const deviceId = String(row.deviceId || '').trim();
    const composedSwitchId = composeSwitchId(row);

    if (!deviceId) {
      setGlobalMessage({
        type: 'error',
        text: `Switch ${index + 1}: Enter a Device ID before testing.`,
      });
      return;
    }

    if (!shellyServerURL || !shellyApiKey) {
      setGlobalMessage({
        type: 'error',
        text: 'Please configure the Shelly Cloud Server URL and Authorization Key before testing switches.',
      });
      return;
    }

    setTestResults((prev) => ({
      ...prev,
      [index]: 'testing',
    }));
    setGlobalMessage({ type: 'info', text: `Switch ${index + 1}: Testing...` });

    try {
      const response = await onTestSwitch(composedSwitchId);
      const deviceStatus = Array.isArray(response?.devices) && response.devices.length === 1
        ? response.devices[0]
        : response;
      const messageType = getMessageType(deviceStatus);
      const message = formatDeviceStatusMessage(deviceStatus);
      
      setTestResults((prev) => ({
        ...prev,
        [index]: messageType === 'success' ? 'success' : 'error',
      }));
      setGlobalMessage({
        type: messageType,
        text: `Switch ${index + 1}: ${message}`,
      });
    } catch (error) {
      setTestResults((prev) => ({
        ...prev,
        [index]: 'error',
      }));
      setGlobalMessage({
        type: 'error',
        text: `Switch ${index + 1}: ${error?.message || 'Unable to verify this switch. Please try again.'}`,
      });
    }
  };

  const handleSave = async () => {
    const dedupedSwitches = [];
    const seen = new Set();
    switchRows.forEach((row) => {
      const composed = composeSwitchId(row);
      if (!composed) return;
      const [deviceIdPart, channelPart] = composed.split(':');
      const normalizedKey = `${deviceIdPart.toLowerCase()}:${channelPart}`;
      if (seen.has(normalizedKey)) return;
      seen.add(normalizedKey);
      dedupedSwitches.push(composed);
    });

    setIsSaving(true);
    try {
      await Promise.resolve(onSave(dedupedSwitches));
      setIsSaving(false);
      onClose();
    } catch (error) {
      setGlobalMessage({
        type: 'error',
        text: error?.message || 'Unable to save switches. Please try again.',
      });
      setIsSaving(false);
    }
  };

  if (!isOpen || !court) return null;

  return (
    <div className="fixed inset-0 bg-slate-500 bg-opacity-75 flex items-center justify-center z-50 p-3 sm:p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[min(90vh,720px)] flex flex-col overflow-hidden mx-auto">
        <div className="p-4 sm:p-6 flex flex-col min-h-0 flex-1 overflow-hidden">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-slate-800">
              Configure Switches for {court.court?.toUpperCase()}
            </h3>
            <button onClick={onClose} className="text-slate-600 hover:text-slate-600">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <p className="text-sm text-slate-600 mb-3">
            Add the Shelly Smart Switch Device IDs connected to the floodlights on this court. Channel defaults to 0 when a Device ID is entered. A device can be tested individually before saving.
          </p>

          {globalMessage.text && (
            <div
              className={`mb-3 rounded-md border p-3 text-sm ${
                globalMessage.type === 'success'
                  ? 'bg-green-50 text-green-700 border-green-200'
                  : globalMessage.type === 'error'
                  ? 'bg-red-50 text-red-700 border-red-200'
                  : 'bg-blue-50 text-blue-700 border-blue-200'
              }`}
            >
              {globalMessage.text}
            </div>
          )}

          <div className="space-y-4 sm:space-y-3 min-h-0 flex-1 overflow-y-auto pr-1 -mr-1">
            {switchRows.map((row, index) => {
              const result = testResults[index];
              const testBtnColorClass =
                result === 'testing'
                  ? 'bg-blue-200 text-blue-700 border-blue-300 cursor-not-allowed'
                  : 'bg-blue-100 text-blue-700 border-blue-300 hover:bg-blue-200';
              /** Same outer box as Channel input: 70px × py-2, for alignment on mobile + web */
              const testBtnClass = `inline-flex items-center justify-center shrink-0 w-[4.375rem] px-2 py-2 text-sm font-semibold rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-1 ${testBtnColorClass}`;

              const channelInputClass =
                'w-[4.375rem] shrink-0 px-2 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm text-center';

              const deviceInputClass =
                'w-full min-w-0 px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm';
              const deviceInputFlexRowClass =
                'flex-1 min-w-0 px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm';

              return (
                <div key={index}>
                  {/* Mobile: header row (label + Test), then Device ID + Channel on one row */}
                  <div className="sm:hidden rounded-lg border border-slate-200 bg-slate-50/80 p-3 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-slate-700">Switch {index + 1}</span>
                      <button
                        type="button"
                        onClick={() => handleTest(index)}
                        className={testBtnClass}
                        disabled={result === 'testing'}
                      >
                        {result === 'testing' ? '…' : 'Test'}
                      </button>
                    </div>
                    <div className="flex items-center gap-2 min-w-0">
                      <input
                        type="text"
                        value={row.deviceId}
                        onChange={(e) => handleDeviceChange(index, e.target.value)}
                        placeholder="Device ID"
                        className={deviceInputFlexRowClass}
                      />
                      <input
                        type="text"
                        inputMode="numeric"
                        value={row.channel}
                        onChange={(e) => handleChannelChange(index, e.target.value)}
                        placeholder="Channel"
                        className={channelInputClass}
                        disabled={!String(row.deviceId || '').trim()}
                      />
                    </div>
                  </div>

                  {/* sm+: single row; label & Test narrower than before; Device ID grows with min width in grid */}
                  <div className="hidden sm:grid sm:items-center sm:gap-2 md:gap-3 sm:grid-cols-[5rem_minmax(12rem,1fr)_4.375rem_4.375rem] md:grid-cols-[6.5rem_minmax(12rem,1fr)_4.375rem_4.375rem]">
                    <label className="text-sm font-medium text-slate-600 truncate" title={`Switch ${index + 1}`}>
                      Switch {index + 1}
                    </label>
                    <input
                      type="text"
                      value={row.deviceId}
                      onChange={(e) => handleDeviceChange(index, e.target.value)}
                      placeholder="Device ID"
                      className={deviceInputClass}
                    />
                    <input
                      type="text"
                      inputMode="numeric"
                      value={row.channel}
                      onChange={(e) => handleChannelChange(index, e.target.value)}
                      placeholder="Channel"
                      className={channelInputClass}
                      disabled={!String(row.deviceId || '').trim()}
                    />
                    <button
                      type="button"
                      onClick={() => handleTest(index)}
                      className={testBtnClass}
                      disabled={result === 'testing'}
                      title={result === 'testing' ? 'Testing' : 'Test switch'}
                    >
                      {result === 'testing' ? '…' : 'Test'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-4 sm:mt-6 flex justify-end gap-2 sm:gap-3 pt-2 border-t border-slate-100 sm:border-0 sm:pt-0 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-md shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className={`inline-flex items-center px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                isSaving ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {isSaving ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white mr-2" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Saving
                </>
              ) : (
                'Save Switches'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SwitchesModal;

